// src/components/auth/VerificacaoCode.jsx
import React, {useEffect, useRef, useState, useCallback} from "react";
import Style from "../../styles/components/auth/Entrada.module.scss";
import Loader from "../../design/components/Loader/Loader";
import { FiCheck, FiClock, FiMail, FiPhone } from "react-icons/fi";
import {AiOutlineArrowLeft} from "react-icons/ai";
import LogoEscuroDois from "../../Assets/images/logo/escuroGrad.svg";
import {getFunctions, httpsCallable} from "firebase/functions";
import {signInWithCustomToken} from "firebase/auth";
import {auth} from "../../firebaseConfig";
import {Modal} from "react-bootstrap";
import { logRegistrationCompleted } from "../../services/analyticsService";
import { useNavigate, useLocation } from "react-router-dom";
import { distribuirDigitos } from "../../utils/otpDigitos";

const VerificacaoCode = ({
  sessionId,
  hint,
  eventKey,
  setStep,
  onResend,
}) => {
  const [codigo, setCodigo] = useState(["", "", "", "", "", ""]);
  const [canResend, setCanResend] = useState(false);
  const [timer, setTimer] = useState(60);
  const [erro, setErro] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // Terceira etapa do checklist da tela "Verificando". É só leitura de
  // progresso: acende quando o servidor já confirmou o código e o app está
  // montando a sessão. Não decide nada.
  const [etapaPerfil, setEtapaPerfil] = useState(false);
  const isSubmittingRef = useRef(false);
  /* Código que JÁ foi ao servidor. A sessão do OTP é de uso único: mandar o
     mesmo código duas vezes devolve 400 "Sessão já utilizada/expirada". */
  const codigoEnviadoRef = useRef("");

  const navigate = useNavigate();
  const inputsRef = useRef([]);
  const functions = getFunctions(undefined, "southamerica-east1");
  const verifyCode = httpsCallable(functions, "verifyCode");

  const MAX_FRONT_ATTEMPTS = 5;
  const [attempts, setAttempts] = useState(0);
  const [blocked, setBlocked] = useState(false);

  const [showGymReferralModal, setShowGymReferralModal] = useState(false);
  const [gymReferralAction, setGymReferralAction] = useState(null);
  // "link" | "switch" | null

  const [currentGym, setCurrentGym] = useState(null);
  const [newGym, setNewGym] = useState(null);
  const getGymReferralStatus = httpsCallable(functions, "getGymReferralStatus");
  const applyGymReferralToUser = httpsCallable(functions, "applyGymReferralToUser");

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const getStoredGymReferral = () => {
    try {
      const raw = localStorage.getItem("LESVITA_GYM_REF");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error("Erro ao ler LESVITA_GYM_REF:", e);
      return null;
    }
  };

  const getStoredTeamJoin = () => {
    try {
      const raw = localStorage.getItem("LESVITA_TEAM_JOIN");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      console.error("Erro ao ler LESVITA_TEAM_JOIN:", e);
      return null;
    }
  };

  // Navega para /bem-vindo (cadastro) ou /plataforma/pessoal (login)
  // gymData opcional: { gymName, gymLogo }
  const navigateAfterAuth = (gymData = null) => {
    localStorage.removeItem("LESVITA_GYM_REF");
    localStorage.removeItem("LESVITA_TEAM_JOIN");

    if (eventKey === "cadastro") {
      // Etapa "Tem um código de criador?" (Programa Criadores Parceiros) fica
      // entre o código e as boas-vindas. A rota decide sozinha se aparece
      // (flag CRIADORES_ATIVO) e, se não, segue direto para /bem-vindo com o
      // mesmo state.
      navigate("/codigo-criador", {
        state: {
          gymName: gymData?.gymName || null,
          gymLogo: gymData?.gymLogo || null,
        },
        replace: true,
      });
    } else {
      navigate("/plataforma/pessoal", {replace: true});
    }
  };

  // ─── Confirmar código ────────────────────────────────────────────────────────

  const confirmarCodigo = useCallback(async (code) => {
    if (blocked) return;

    if (isSubmittingRef.current) return;

    setErro("");
    if (!sessionId) {
      setErro("Sessão inválida. Reenvie o código.");
      return;
    }

    try {
      isSubmittingRef.current = true;
      codigoEnviadoRef.current = code;
      setIsLoading(true);

      const res = await verifyCode({sessionId, code});
      const data = res?.data || {};

      if (data.status !== "ok" || !data.customToken) {
        setAttempts((prev) => {
          const next = prev + 1;
          if (next >= MAX_FRONT_ATTEMPTS) {
            setBlocked(true);
          }
          return next;
        });

        setErro("Código inválido ou sessão expirada.");
        setEtapaPerfil(false);
        setIsLoading(false);
        isSubmittingRef.current = false;
        // Libera o MESMO código para nova tentativa: o servidor recusou, então
        // ele nunca foi consumido — e a pessoa pode muito bem redigitar igual.
        codigoEnviadoRef.current = "";
        setCodigo(["", "", "", "", "", ""]);
        inputsRef.current[0]?.focus();
        return;
      }

      // ── Login Firebase ───────────────────────────────────────────────────────
      setEtapaPerfil(true);
      await signInWithCustomToken(auth, data.customToken);
      if (eventKey === "cadastro") logRegistrationCompleted("organico");

      // ── Para login: vai direto, sem tela de boas-vindas ─────────────────────
      if (eventKey === "login") {
        setIsLoading(false);
        // O cadeado NÃO é solto aqui: a sessão do OTP já foi consumida e a
        // tela está saindo. Soltá-lo fazia o efeito de auto-submit disparar de
        // novo (ele depende de `isLoading`) com o mesmo código, e o servidor
        // respondia 400 "Sessão já utilizada/expirada" — o erro que aparecia
        // no console DEPOIS de o login dar certo.
        navigate("/plataforma/pessoal", {replace: true});
        return;
      }

      // ── Para cadastro: verifica/exibe modal de gym referral ──────────────────
      const referral = getStoredGymReferral();

      if (!referral?.referralCode) {
        // Sem referral: vai para /bem-vindo sem dados de gym
        setIsLoading(false);
        // O cadeado NÃO é solto aqui: a sessão do OTP já foi consumida e a
        // tela está saindo. Soltá-lo fazia o efeito de auto-submit disparar de
        // novo (ele depende de `isLoading`) com o mesmo código, e o servidor
        // respondia 400 "Sessão já utilizada/expirada" — o erro que aparecia
        // no console DEPOIS de o login dar certo.
        navigateAfterAuth(null);
        return;
      }

      // Tem referral: consulta status antes de vincular
      const statusRes = await getGymReferralStatus({
        referralCode: referral.referralCode,
      });
      const statusData = statusRes?.data || {};

      if (!statusData?.hasUser) {
        // Usuário não encontrado no status (raro, mas seguro)
        setIsLoading(false);
        // O cadeado NÃO é solto aqui: a sessão do OTP já foi consumida e a
        // tela está saindo. Soltá-lo fazia o efeito de auto-submit disparar de
        // novo (ele depende de `isLoading`) com o mesmo código, e o servidor
        // respondia 400 "Sessão já utilizada/expirada" — o erro que aparecia
        // no console DEPOIS de o login dar certo.
        navigateAfterAuth(null);
        return;
      }

      // Novo vínculo
      if (!statusData?.currentGymReferral?.APPLIED) {
        setNewGym({
          code: referral.referralCode,
          gymName: referral.gymName,
          city: referral.city,
          stateUf: referral.stateUf,
          partnerUser: referral.partnerUser,
        });
        setGymReferralAction("link");
        setIsLoading(false);
        // O cadeado NÃO é solto aqui: a sessão do OTP já foi consumida e a
        // tela está saindo. Soltá-lo fazia o efeito de auto-submit disparar de
        // novo (ele depende de `isLoading`) com o mesmo código, e o servidor
        // respondia 400 "Sessão já utilizada/expirada" — o erro que aparecia
        // no console DEPOIS de o login dar certo.
        setShowGymReferralModal(true);
        return;
      }

      // Troca de academia
      if (
        statusData?.currentGymReferral?.CODE &&
        statusData.currentGymReferral.CODE !== referral.referralCode
      ) {
        setCurrentGym({
          code: statusData.currentGymReferral.CODE,
          gymName: statusData.currentGymReferral.GYM_NAME,
          city: statusData.currentGymReferral.CITY,
          stateUf: statusData.currentGymReferral.STATE_UF,
        });
        setNewGym({
          code: referral.referralCode,
          gymName: referral.gymName,
          city: referral.city,
          stateUf: referral.stateUf,
          partnerUser: referral.partnerUser,
        });
        setGymReferralAction("switch");
        setIsLoading(false);
        // O cadeado NÃO é solto aqui: a sessão do OTP já foi consumida e a
        // tela está saindo. Soltá-lo fazia o efeito de auto-submit disparar de
        // novo (ele depende de `isLoading`) com o mesmo código, e o servidor
        // respondia 400 "Sessão já utilizada/expirada" — o erro que aparecia
        // no console DEPOIS de o login dar certo.
        setShowGymReferralModal(true);
        return;
      }

      // Mesma academia — já estava vinculado, vai direto
      setIsLoading(false);
      // Idem: sessão consumida, cadeado fica fechado.
      navigateAfterAuth({gymName: referral.gymName, gymLogo: referral.logoUrl});
    } catch (e) {
      console.error("Erro ao verificar código:", e);

      const rawMsg = e?.message || "";

      // O servidor reusa códigos gRPC (ex.: failed-precondition = sessão E idade),
      // então classificamos pela MENSAGEM, que é específica e amigável.
      const isExpiredSession =
        rawMsg.includes("Sessão já utilizada") ||
        rawMsg.includes("Sessão não encontrada");
      const isExpiredCode = rawMsg.includes("Código expirado");
      const isTooMany = rawMsg.includes("Muitas tentativas");
      const isWrongCode = rawMsg.includes("Código inválido");

      // Só conta como tentativa quando o código digitado está errado.
      // Falhas de rede/servidor e erros de negócio não bloqueiam o usuário.
      if (isWrongCode) {
        setAttempts((prev) => {
          const next = prev + 1;
          if (next >= MAX_FRONT_ATTEMPTS) setBlocked(true);
          return next;
        });
      }

      if (isExpiredSession || isTooMany) {
        setBlocked(true);
      }

      if (isExpiredSession) {
        setErro(
          "Sessão expirada. Toque em \"Reenviar Código\" para receber um novo.",
        );
      } else if (isTooMany) {
        setErro(
          "Muitas tentativas. Toque em \"Reenviar Código\" para recomeçar.",
        );
      } else if (isExpiredCode) {
        setErro("Código expirado. Toque em \"Reenviar Código\".");
      } else if (isWrongCode) {
        setErro("Código inválido. Tente novamente.");
      } else if (rawMsg && !/internal|unavailable|deadline|network/i.test(rawMsg)) {
        // Erro de negócio com mensagem pronta do servidor
        // (ex.: "Este telefone já está cadastrado. Faça login.", idade mínima).
        setErro(rawMsg);
      } else {
        setErro("Não foi possível verificar o código. Tente novamente.");
      }

      isSubmittingRef.current = false;
      codigoEnviadoRef.current = "";
      setIsLoading(false);
      setCodigo(["", "", "", "", "", ""]);
      inputsRef.current[0]?.focus();
    }
  }, [sessionId, verifyCode, navigate, blocked, attempts, eventKey]);

  // ─── Confirmar vínculo de academia (modal) ───────────────────────────────────

  const handleConfirmGymReferral = async () => {
    try {
      setIsLoading(true);

      // Lê ANTES de remover
      const referral = getStoredGymReferral();

      if (!referral?.referralCode) {
        setIsLoading(false);
        setShowGymReferralModal(false);
        navigateAfterAuth(null);
        return;
      }

      const teamJoin = getStoredTeamJoin();

      await applyGymReferralToUser({
        referralCode: referral.referralCode,
        gymName: referral.gymName || null,
        city: referral.city || null,
        stateUf: referral.stateUf || null,
        origin: referral.origin || "GYM",
        partnerUser: referral.partnerUser === true,
        capturedAt: referral.capturedAt || Date.now(),
        logoUrl: referral.logoUrl || null,
        teamCode: teamJoin?.teamCode || null,
      });

      setShowGymReferralModal(false);
      // navigateAfterAuth já remove o localStorage
      navigateAfterAuth({gymName: referral.gymName, gymLogo: referral.logoUrl});
    } catch (e) {
      console.error("Erro ao aplicar vínculo da academia:", e);
      setErro("Não foi possível atualizar o vínculo com a academia.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelGymReferral = () => {
    setShowGymReferralModal(false);
    // navigateAfterAuth já remove o localStorage
    navigateAfterAuth(null);
  };

  // ─── Auto-submit ao completar 6 dígitos ─────────────────────────────────────

  useEffect(() => {
    const code = codigo.join("");
    /* `codigoEnviadoRef` é a segunda tranca: o efeito reage a `isLoading`, que
       muda no fim de TODO caminho de sucesso. Sem ela, qualquer retorno que
       desligue o spinner reenviaria o código já consumido. */
    if (code.length === 6 && !isLoading && codigoEnviadoRef.current !== code) {
      confirmarCodigo(code);
    }
  }, [codigo, confirmarCodigo, isLoading]);

  // ─── Timer de reenvio ────────────────────────────────────────────────────────

  useEffect(() => {
    if (timer === 0) {
      setCanResend(true);
      return;
    }
    const i = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(i);
  }, [timer]);

  // ─── Handlers de input ───────────────────────────────────────────────────────

  /* Aceita 1 dígito, o código inteiro (autofill do teclado / colagem) ou um
     pedaço dele — `distribuirDigitos` espalha pelas caixas. Antes, o regex
     `^\d?$` descartava qualquer valor com mais de um caractere, e o autofill
     do iOS/Android (que cola os 6 de uma vez na caixa focada) deixava só o
     primeiro número. */
  const handleChange = (index, value) => {
    const { proximo, foco } = distribuirDigitos(codigo, index, value);
    setCodigo(proximo);
    const completo = proximo.every(Boolean);
    if (completo) inputsRef.current[5]?.blur(); // fecha o teclado: o auto-submit assume
    else if (foco !== index || proximo[index]) inputsRef.current[foco]?.focus();
  };

  const handlePaste = (index, e) => {
    const texto = e.clipboardData?.getData("text") || "";
    if (!/\d/.test(texto)) return;
    e.preventDefault();
    handleChange(index, texto);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (codigo[index]) {
        const next = [...codigo];
        next[index] = "";
        setCodigo(next);
      } else if (index > 0) {
        inputsRef.current[index - 1]?.focus();
        const next = [...codigo];
        next[index - 1] = "";
        setCodigo(next);
      }
    }
  };

  const handleBack = () => {
    if (eventKey === "cadastro") setStep(2);
    else if (eventKey === "login") setStep(1);
  };

  const handleResend = async () => {
    if (!onResend || !canResend || isLoading) return;
    setErro("");
    setIsLoading(true);
    try {
      const result = await onResend();
      if (result && result.ok === false) {
        setErro(result.message || "Não foi possível reenviar o código.");
        return;
      }
      // Sucesso: zera o estado da tela para o novo código/sessão.
      setCodigo(["", "", "", "", "", ""]);
      setAttempts(0);
      setBlocked(false);
      setTimer(60);
      setCanResend(false);
      inputsRef.current[0]?.focus();
    } catch (err) {
      console.error("Erro ao reenviar código:", err);
      setErro("Não foi possível reenviar o código. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };
  const location = useLocation();

  const gymName = location.state?.gymName || null;
  const gymLogo = location.state?.gymLogo || null;

  // ─── Render ──────────────────────────────────────────────────────────────────

  // O código completo dispara a verificação sozinho (efeito acima). O CTA do
  // desenho existe para o caso em que isso falhou: ele chama a MESMA função,
  // com a MESMA guarda de isLoading, e só fica ativo com os 6 dígitos.
  const codigoCompleto = codigo.join("").length === 6;

  return (
    <div className={Style.tela}>
      <span className={Style.fundoInterno} aria-hidden="true" />

      <div className={Style.topo}>
        <div className={Style.topoLinha}>
          <button type="button" className={Style.voltarRedondo} onClick={handleBack} aria-label="Voltar">
            <AiOutlineArrowLeft size={18} />
          </button>
          <span className={Style.contador}>
            <FiClock size={13} />
            <span className={Style.contadorTexto}>
              {`${String(Math.floor(timer / 60)).padStart(2, "0")}:${String(timer % 60).padStart(2, "0")}`}
            </span>
          </span>
        </div>

        <h1 className={Style.tituloGrande}>Confirme o código</h1>
        <p className={Style.apoio}>
          Enviamos 6 dígitos por WhatsApp e e-mail. O teclado numérico do
          aparelho abre ao tocar nas caixas.
        </p>
      </div>

      <div className={Style.corpoCodigo}>
        <div className={Style.digitos}>
          {codigo.map((digit, idx) => (
            <input
              key={idx}
              ref={(el) => (inputsRef.current[idx] = el)}
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              /* `one-time-code` em TODAS as caixas: o teclado só sugere o
                 código para o campo focado, e a pessoa nem sempre toca na
                 primeira. Sem `maxLength`: ele cortava o autofill para um
                 caractere antes de o handler ver o código. */
              name={`codigo-${idx + 1}`}
              autoComplete="one-time-code"
              value={digit}
              onChange={(e) => handleChange(idx, e.target.value)}
              onPaste={(e) => handlePaste(idx, e)}
              onKeyDown={(e) => handleKeyDown(e, idx)}
              className={`${Style.digito} ${digit ? Style.digitoCheio : ""}`}
              aria-label={`Dígito ${idx + 1} de 6`}
            />
          ))}
        </div>

        {erro && <p className={Style.erro} style={{ marginTop: 12 }}>{erro}</p>}

        <div className={Style.destinos}>
          <div className={Style.destino}>
            <span className={Style.destinoIcone} style={{ background: "rgba(62,207,142,.13)" }}>
              <FiPhone size={16} color="#3ECF8E" />
            </span>
            <span className={Style.destinoCorpo}>
              <span className={Style.destinoRotulo}>WHATSAPP</span>
              <span className={Style.destinoValor}>{hint?.contatoMasked || "—"}</span>
            </span>
            <button
              type="button"
              className={`${Style.reenviar} ${canResend ? Style.reenviarAtivo : ""}`}
              onClick={handleResend}
              disabled={!canResend}
            >
              {canResend ? "reenviar" : "aguarde"}
            </button>
          </div>

          <div className={Style.destino}>
            <span className={Style.destinoIcone} style={{ background: "rgba(143,162,255,.13)" }}>
              <FiMail size={16} color="#8FA2FF" />
            </span>
            <span className={Style.destinoCorpo}>
              <span className={Style.destinoRotulo}>E-MAIL</span>
              <span className={Style.destinoValor}>{hint?.emailMasked || "—"}</span>
            </span>
            <button
              type="button"
              className={`${Style.reenviar} ${canResend ? Style.reenviarAtivo : ""}`}
              onClick={handleResend}
              disabled={!canResend}
            >
              {canResend ? "reenviar" : "aguarde"}
            </button>
          </div>
        </div>

        <span className={Style.espaco} />

        <button
          type="button"
          className={Style.primarioGrande}
          onClick={() => codigoCompleto && !isLoading && confirmarCodigo(codigo.join(""))}
          disabled={!codigoCompleto || isLoading || blocked}
        >
          {isLoading ? <Loader tamanho={28} /> : "Verificar código"}
        </button>
      </div>

      {/* ── Modal de vínculo de academia ── */}
      <Modal
        show={showGymReferralModal}
        centered
        backdrop="static"
        keyboard={false}
        onHide={handleCancelGymReferral}
      >
        <Modal.Header>
          <Modal.Title>
            {gymReferralAction === "switch"
              ? "Trocar vínculo de academia"
              : "Vincular academia"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {gymReferralAction === "switch" ? (
            <>
              <p>
                Você já possui vínculo com{" "}
                <strong>{currentGym?.gymName || "sua academia atual"}</strong>.
                Deseja trocar para{" "}
                <strong>{newGym?.gymName || "a nova academia"}</strong>?
              </p>
              <div style={{fontWeight: 600, marginTop: 12}}>
                {currentGym?.gymName || "Academia atual"} →{" "}
                {newGym?.gymName || "Nova academia"}
              </div>
            </>
          ) : (
            <p>
              Deseja vincular sua conta com{" "}
              <strong>{newGym?.gymName || "esta academia"}</strong>?
            </p>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            onClick={handleCancelGymReferral}
            disabled={isLoading}
          >
            Agora não
          </button>
          <button
            type="button"
            onClick={handleConfirmGymReferral}
            disabled={isLoading}
          >
            {isLoading ? "Confirmando..." : "Confirmar"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Verificando (Entrada.dc.html, tela `verificando`) ──
          O checklist NÃO é decorativo: cada etapa reflete o que já
          aconteceu de verdade. "CPF validado" é fato desde que a tela
          abriu (foi ele que gerou a sessão); "Código confirmado" acende
          quando os 6 dígitos foram enviados; "Preparando seu perfil"
          acende quando a verificação voltou e o app está montando a
          sessão. Nenhum timer inventado. */}
      {isLoading && (
        <div className={Style.telaSobreposta}>
          <span className={Style.fundoInterno} aria-hidden="true" />
          <div className={Style.centro}>
            <div className={Style.cartao}>
              <div className={Style.cartaoTopo}>
                <Loader tamanho={76} />
                <div className={Style.cartaoTextos}>
                  <span className={Style.cartaoTitulo}>Verificando…</span>
                  <span className={Style.cartaoApoio}>Isso leva poucos segundos</span>
                </div>
              </div>

              <div className={Style.divisor} />

              <div className={Style.checklist}>
                {[
                  ["CPF validado", true],
                  ["Código confirmado", codigo.join("").length === 6],
                  ["Preparando seu perfil", etapaPerfil],
                ].map(([nome, feita]) => (
                  <div key={nome} className={Style.etapa}>
                    <span className={`${Style.etapaIcone} ${feita ? Style.etapaFeita : ""}`}>
                      {feita && <FiCheck size={13} />}
                    </span>
                    <span className={`${Style.etapaTexto} ${feita ? Style.etapaTextoFeita : ""}`}>
                      {nome}
                    </span>
                  </div>
                ))}
              </div>

              <div className={Style.trilha}>
                <div
                  className={Style.progresso}
                  style={{
                    width: `${etapaPerfil ? 100 : codigo.join("").length === 6 ? 67 : 34}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificacaoCode;